import type { HolidayPolicy, RecurrenceFreq, RecurrenceRule } from '@taskos/shared';
import { useId } from 'react';
import { Field, Segmented } from '../../components/ui';

const FREQS: Array<{ value: RecurrenceFreq; label: string }> = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'YEARLY', label: 'Yearly' },
  { value: 'INTERVAL_DAYS', label: 'Every N days' },
  { value: 'WEEKDAYS', label: 'Weekdays' },
  { value: 'WORKING_DAYS', label: 'Working days' },
];
const WD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function RecurrencePicker({ value, onChange }: { value: RecurrenceRule; onChange: (r: RecurrenceRule) => void }) {
  const id = useId();
  const set = (patch: Partial<RecurrenceRule>) => onChange({ ...value, ...patch });
  const needsInterval = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY', 'INTERVAL_DAYS', 'WORKING_DAYS'].includes(value.freq);
  const unit = { DAILY: 'day(s)', WEEKLY: 'week(s)', MONTHLY: 'month(s)', YEARLY: 'year(s)', INTERVAL_DAYS: 'days', WORKING_DAYS: 'working day(s)', WEEKDAYS: '' }[value.freq];
  return (
    <div className="stack sm">
      <span className="label">Repeats</span>
      <Segmented label="Frequency" value={value.freq} onChange={(freq) => set({ freq, weekdays: freq === 'WEEKLY' ? value.weekdays : null })} options={FREQS} />
      {needsInterval && (
        <label className="inline-row small" htmlFor={`${id}-int`}>
          Every
          <input
            id={`${id}-int`}
            className="input"
            type="number"
            min={1}
            max={3650}
            inputMode="numeric"
            style={{ width: 90 }}
            value={value.interval}
            onChange={(e) => set({ interval: Math.max(1, Math.min(3650, Number(e.target.value) || 1)) })}
          />
          {unit}
        </label>
      )}
      {value.freq === 'WEEKLY' && (
        <div className="chip-row" role="group" aria-label="Weekdays">
          {WD.map((d, i) => {
            const n = i + 1;
            const on = value.weekdays?.includes(n) ?? false;
            return (
              <button
                key={d}
                type="button"
                className="chip"
                aria-pressed={on}
                onClick={() => {
                  const cur = new Set(value.weekdays ?? []);
                  if (on) cur.delete(n);
                  else cur.add(n);
                  set({ weekdays: [...cur].sort() });
                }}
              >
                {d}
              </button>
            );
          })}
        </div>
      )}
      <div className="form-grid">
        <Field label="Starts" htmlFor={`${id}-start`}>
          <input id={`${id}-start`} type="date" className="input" value={value.startDate} required onChange={(e) => e.target.value && set({ startDate: e.target.value })} />
        </Field>
        <Field label="Time (optional)" htmlFor={`${id}-time`}>
          <input id={`${id}-time`} type="time" className="input" value={value.time ?? ''} onChange={(e) => set({ time: e.target.value || null })} />
        </Field>
        <Field label="Ends (optional)" htmlFor={`${id}-end`} hint="Leave empty to repeat forever">
          <input id={`${id}-end`} type="date" className="input" value={value.endDate ?? ''} min={value.startDate} onChange={(e) => set({ endDate: e.target.value || null })} />
        </Field>
        {value.freq !== 'WORKING_DAYS' && (
          <Field label="On holidays / off days" htmlFor={`${id}-hp`}>
            <select id={`${id}-hp`} className="select" value={value.holidayPolicy} onChange={(e) => set({ holidayPolicy: e.target.value as HolidayPolicy })}>
              <option value="NONE">Keep the date</option>
              <option value="NEXT_WORKING_DAY">Move to next working day</option>
              <option value="PREVIOUS_WORKING_DAY">Move to previous working day</option>
              <option value="SKIP">Skip that occurrence</option>
            </select>
          </Field>
        )}
      </div>
      {(value.freq === 'MONTHLY' || value.freq === 'YEARLY') && Number(value.startDate.slice(8)) > 28 && (
        <div className="inline-alert info small">Months without day {Number(value.startDate.slice(8))} use their last day (e.g. 28/29 Feb).</div>
      )}
    </div>
  );
}
